import React from "react";
import classNames from "classnames";
import Close from "../../../assets/svgs/Close.svg";
import DynamicComponent from "../../utils/DynamicComponent/DynamicComponent.component";
import Blok from "../../../interfaces/Storyblok/Blok.interface";
import { ButtonProps } from "../../../interfaces/Button.interface";
import {
  StickyPanelPlacement,
  StoryblokStickyPanelButton,
} from "../../../interfaces/Storyblok/StoryblokStickyPanelButton.interface";
import {
  EnquiryIcon,
  getIconUrl,
  positionConfig,
  resolveIconPosition,
  resolvePosition,
  usePanelAnimation,
} from "../StickyButton/stickyPanel.shared";

const hasLabel = (buttons: ButtonProps[]): boolean =>
  Array.isArray(buttons) && buttons.length > 0 && !!buttons[0].label;

// Blank/unset placement falls back to "center" (the authored default).
const resolvePlacement = (
  placement: StickyPanelPlacement | StickyPanelPlacement[] | undefined,
): StickyPanelPlacement => (Array.isArray(placement) ? placement[0] : placement) || "center";

const StickyPanelButton: React.FC<StoryblokStickyPanelButton> = ({
  sticky_button,
  position,
  panel_placement,
  panel_content = [],
  panel_aria_label,
  custom_icon,
  icon_position,
  custom_color,
}) => {
  const { isPanelOpen, isPanelMounted, isPanelVisible, openPanel, closePanel, closeButtonRef } = usePanelAnimation();

  if (!hasLabel(sticky_button)) {
    return null;
  }

  const button = sticky_button[0];
  const config = positionConfig[resolvePosition(position)] ?? positionConfig["bottom-right"];
  const isCentered = resolvePlacement(panel_placement) === "center";

  const customColor = custom_color?.trim() ? custom_color.trim() : "#ED3E42";
  // Applied inline because Tailwind can only generate arbitrary values it sees at build time,
  // so an authored colour can never come through as a bg-[...] class.
  const tabStyle: React.CSSProperties = { backgroundColor: customColor };

  const tabClassName = classNames(
    "group flex flex-col items-center gap-[10px] px-[9px] py-[21px] text-White shadow-2xl transition-all duration-300 ease-in-out hover:brightness-95 focus:outline-none focus:ring-0",
    config.rounded,
  );

  const customIconUrl = getIconUrl(custom_icon);
  const selectedIconPosition = resolveIconPosition(icon_position);

  const labelContent = (
    <span
      className="font-extrabold uppercase tracking-[0.25em] text-sm leading-none"
      style={{
        writingMode: "vertical-rl",
        transform: config.readUpwards ? "rotate(180deg)" : undefined,
      }}
    >
      {button.label}
    </span>
  );

  const iconContent = customIconUrl ? (
    <img
      src={customIconUrl}
      // Decorative: the tab already takes its accessible name from the visible label.
      alt=""
      aria-hidden="true"
      style={{ transform: `rotate(${config.readUpwards ? 270 : 90}deg)` }}
      className="h-6 w-6 shrink-0 object-contain"
    />
  ) : (
    <EnquiryIcon flip={!config.readUpwards} />
  );

  // The column stacks top-to-bottom, but the label reads bottom-to-top on right-hand tabs, so there
  // the leading icon has to come last in the DOM to end up ahead of the words.
  const isIconFirst = config.readUpwards ? selectedIconPosition === "trailing" : selectedIconPosition === "leading";

  return (
    <>
      <div className={classNames("fixed z-50", config.container)}>
        <button
          type="button"
          onClick={openPanel}
          className={tabClassName}
          style={tabStyle}
          aria-expanded={isPanelOpen}
        >
          {isIconFirst && iconContent}
          {labelContent}
          {!isIconFirst && iconContent}
        </button>
      </div>

      {isPanelMounted && (
        <div
          className={classNames(
            "fixed inset-0 z-[60] overflow-hidden bg-[rgba(0,0,0,0.5)] p-4 transition-opacity duration-300 ease-out motion-reduce:transition-none",
            // Centered: always middle of the viewport. Corner: middle on mobile, anchored from sm up.
            isCentered ? "flex items-center justify-center" : "flex items-center justify-center sm:block sm:p-0",
            // Stops the backdrop swallowing clicks during the 300ms slide-out.
            isPanelVisible ? "opacity-100" : "pointer-events-none opacity-0",
          )}
          onClick={closePanel}
          role="presentation"
        >
          <div
            className={classNames(
              "relative w-full transition-transform duration-300 ease-out motion-reduce:transition-none",
              // Centered floats in the middle; corner anchors to the tab (as the contact-card panel does).
              isCentered ? "max-w-xl" : "max-w-[402.5px] sm:absolute",
              !isCentered && config.panel,
              // Reuses the slide direction from the tab's corner for both placements.
              isPanelVisible ? "translate-x-0" : config.panelClosed,
            )}
            onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={panel_aria_label?.trim() || button.label}
          >
            <button
              ref={closeButtonRef}
              type="button"
              onClick={closePanel}
              aria-label="Close"
              // Same floating white-circle affordance as the original StickyButton popover: sits above the
              // panel's top-right in both placements rather than overlapping the content.
              className="absolute -top-11 right-0 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-White text-Primary-900 shadow-lg transition-transform duration-300 hover:scale-110"
            >
              <Close className="h-3.5 w-3.5" />
            </button>

            {/*-- Rounded card clips the corners; the inner wrapper scrolls (while the page behind
                 stays scroll-locked) so the thin scrollbar stays inside the card and never bleeds
                 into the 24px rounded corners. --*/}
            <div className="overflow-hidden rounded-[24px] bg-White">
              <div className="scrollbar-modal max-h-[85vh] overflow-y-auto overscroll-contain">
                {panel_content.map((blok: Blok) => (
                  <DynamicComponent key={blok._uid} blok={blok} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StickyPanelButton;
